import React, { useEffect, useState } from 'react';
import Modal from './Modal';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCirclePlus,
    faCircleMinus,
    faHistory,
    faArrowUp, faArrowDown,
    faXmark,
    faTriangleExclamation,
    faCircleCheck,
    faLayerGroup,
    faPencil,
    faTrash,
    faFilter,
} from '@fortawesome/free-solid-svg-icons';
import Table from '../Listing/Table';
import InputText from '../Form/Fields/InputText';
import InputSliderRange from '../Form/Fields/InputSliderRange';
import InputDateRange from '../Form/Fields/InputDateRange';
import StockBatchMovementModal from './StockBatchMovementModal'
import StockEditModal from './StockEditModal';
import { API_BASE_URL } from '../../api';
import EditBatchModal from './EditBatchModal';
import CreateStockModal from './CreateStockModal';
import { formatRelativeDate, formatExpirationDate } from '../../utils/FormatDate';
import { useAuth } from '../../context/AuthProvider';

const isExpiredBatch = (batch) => {
  if (!batch.expirationDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(batch.expirationDate) < today;
};

// Same session-scoped persistence approach as EntityTable's `entityTableState:` key, keyed by
// product instead of route since this modal is one shared instance reused across every product's
// "Voir les lots" — the modal fully remounts its Table on each open (ReactModal unmounts on
// close), so reading this at render time is enough to restore filters on the next open/refresh.
const STOCK_FILTERS_STORAGE_PREFIX = 'viewStockFilters:';
const readPersistedFilters = (productId) => {
  try {
    const raw = sessionStorage.getItem(`${STOCK_FILTERS_STORAGE_PREFIX}${productId}`);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
};
const persistFilters = (productId, values) => {
  try { sessionStorage.setItem(`${STOCK_FILTERS_STORAGE_PREFIX}${productId}`, JSON.stringify(values)); } catch {}
};

export default function ViewStockModal({ show, onClose, productId, productName, onBatchUpdate }) {
  const { hasRight } = useAuth();
  const canEdit = hasRight('PRODUCTS_EDIT');
  const [selectedBatches, setSelectedBatches] = useState([]);
  const [stockBatchMovementModal, setStockBatchMovementModal] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState(null);
  const [movementType, setMovementType] = useState(null);
  const [createMovementModal, setCreateMovementModal] = useState(false);
  const [editBatchModal, setEditBatchModal] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [createBatchModal, setCreateBatchModal] = useState(false);
  const [showExpiredBatches, setShowExpiredBatches] = useState(false);

  // Fetched once with a large page size rather than paginated — this modal
  // already sorts/filters the batches entirely client-side (expiry ranking,
  // the "lots périmés" toggle), so it needs the full list in memory anyway.
  // (The previous version called this endpoint with no page/size params at
  // all, silently defaulting to the backend's first 10 results — any product
  // with more than 10 lots had the rest invisibly missing.)
  const fetchProductBatches = () => {
    axios.get(`${API_BASE_URL}/api/v1/stock/orderable/${productId}/batches`, {
      withCredentials: true,
      params: { page: 0, size: 1000 },
    })
      .then(response => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const rank = (batch) => {
          if (!batch.expirationDate) return 2;
          return new Date(batch.expirationDate) < today ? 1 : 0;
        };
        const sorted = [...response.data.content].sort((a, b) => {
          const rankDiff = rank(a) - rank(b);
          if (rankDiff !== 0) return rankDiff;
          if (!a.expirationDate || !b.expirationDate) return 0;
          return new Date(a.expirationDate) - new Date(b.expirationDate);
        });
        setSelectedBatches(sorted);
      })
      .catch(error => {
        console.error("Failed to fetch product details", error);
      });
  };

  const handleBatchMovement = (batchId) => {
      setStockBatchMovementModal(true);
      setSelectedBatchId(batchId);
  };

  const handleViderLot = (batch) => {
    const qty = Math.max(0, batch.quantity);
    if (qty === 0) return;
    if (!window.confirm(`Vider ce lot (${qty} unité${qty > 1 ? 's' : ''}) ? Cette action est irréversible.`)) return;
    axios.post(`${API_BASE_URL}/api/v1/stock/remove`, null, {
      withCredentials: true,
      params: { batchId: batch.id, quantity: qty, description: 'Retrait lot périmé' },
    })
      .then(() => { fetchProductBatches(); onBatchUpdate?.(); })
      .catch(err => toast.error(err?.response?.data?.message ?? 'Erreur lors du vidage du lot'));
  };

  const handleCreateMovement = (batchId, movementType) => {
    setSelectedBatchId(batchId);
    setMovementType(movementType);
    setCreateMovementModal(true);
  };

  // Bulk equivalent of handleViderLot — only acts on the selected batches
  // that are actually expired with stock, silently skipping the rest.
  const handleBulkVider = async (ids) => {
    const targets = selectedBatches.filter(b => ids.includes(b.id) && isExpiredBatch(b) && b.quantity > 0);
    if (targets.length === 0) {
      toast.info('Aucun lot périmé avec du stock dans la sélection');
      return;
    }
    if (!window.confirm(`Vider ${targets.length} lot${targets.length > 1 ? 's' : ''} périmé${targets.length > 1 ? 's' : ''} ? Cette action est irréversible.`)) return;
    try {
      await Promise.all(targets.map(b => axios.post(`${API_BASE_URL}/api/v1/stock/remove`, null, {
        withCredentials: true,
        params: { batchId: b.id, quantity: Math.max(0, b.quantity), description: 'Retrait lot périmé' },
      })));
      fetchProductBatches();
      onBatchUpdate?.();
    } catch (err) {
      toast.error(err?.response?.data?.message ?? 'Erreur lors du vidage des lots');
    }
  };

  // Quantity filter scale, from the loaded batches (a batch can go negative).
  const quantities = selectedBatches.map(b => Number(b.quantity) || 0);
  const quantityMin = Math.min(0, ...quantities);
  const quantityMax = Math.max(10, ...quantities);

  const columns = [
    {
      Header: "#",
      accessor: "id",
      sortable: true,
      Cell: (row) => <span style={{ fontSize: 14, fontWeight: 500, color: "#9ca3af" }}>{row.id}</span>,
    },
    {
      Header: "Expiration",
      accessor: "expirationDate",
      className: "text-center",
      sortable: true,
      filterable: { component: InputDateRange, label: "Expiration" },
      Cell: (row) => {
        if (!row.expirationDate) return <span className="text-muted">—</span>;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const exp = new Date(row.expirationDate);
        const diffDays = Math.ceil((exp - today) / (1000 * 60 * 60 * 24));
        const isExpired = diffDays < 0;
        const isSoon = diffDays >= 0 && diffDays <= 90;
        const color = isExpired ? "#dc2626" : isSoon ? "#d97706" : "#059669";
        const icon = isExpired ? faTriangleExclamation : isSoon ? faTriangleExclamation : faCircleCheck;
        return (
          <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <span style={{ color, fontWeight: 600, fontSize: 13, display: "inline-flex", alignItems: "center", gap: 5 }}>
              <FontAwesomeIcon icon={icon} style={{ fontSize: 11 }} />
              {formatRelativeDate(row.expirationDate)}
            </span>
            <span style={{ fontSize: 11, color: "#9ca3af", fontWeight: 400 }}>
              {formatExpirationDate(row.expirationDate)}
            </span>
          </span>
        );
      }
    },
    {
      Header: "Quantité",
      accessor: "quantity",
      className: "text-center",
      sortable: true,
      filterable: { component: InputSliderRange, label: "Quantité", min: quantityMin, max: quantityMax, step: 1 },
      Cell: (row) => {
        const qty = row.quantity;
        return (
          <span className={`fw-bold ${qty > 0 ? 'text-success' : qty < 0 ? 'text-danger' : 'text-muted'}`}>
            {qty > 0 && <FontAwesomeIcon className="me-3" icon={faArrowUp} />}
            {qty < 0 && <FontAwesomeIcon className="me-3" icon={faArrowDown} />}
            {qty}
          </span>
        );
      }
    },
    {
      Header: "N° de lot",
      accessor: "batchNumber",
      sortable: true,
      filterable: { component: InputText },
      Cell: (row) => <span className="fw-bold">{row.batchNumber}</span>
    },
    {
      Header: "",
      sortable: false,
        className: "col-actions",
      Cell: (row) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const isExpiredWithStock = row.expirationDate && new Date(row.expirationDate) < today && row.quantity > 0;
        return (
          <div style={{ display: 'flex', gap: 6 }}>
            {canEdit && (
              <button type="button" className="modal-icon-btn-success" onClick={() => handleCreateMovement(row.id, 'INBOUND')} data-tooltip-id="tooltip" data-tooltip-content="Ajouter">
                <FontAwesomeIcon icon={faCirclePlus} />
              </button>
            )}
            {canEdit && (
              <button type="button" className="modal-icon-btn-danger" onClick={() => handleCreateMovement(row.id, 'OUTBOUND')} data-tooltip-id="tooltip" data-tooltip-content="Retirer" disabled={Math.max(0, row.quantity) === 0}>
                <FontAwesomeIcon icon={faCircleMinus} />
              </button>
            )}
            {canEdit && (
              <button type="button" className="modal-icon-btn-neutral" onClick={() => { setSelectedBatch(row); setEditBatchModal(true); }} data-tooltip-id="tooltip" data-tooltip-content="Modifier">
                <FontAwesomeIcon icon={faPencil} />
              </button>
            )}
            <button type="button" className="modal-icon-btn-neutral" onClick={() => handleBatchMovement(row.id)} data-tooltip-id="tooltip" data-tooltip-content="Historique">
              <FontAwesomeIcon icon={faHistory} />
            </button>
            {canEdit && isExpiredWithStock && (
              <button type="button" className="modal-icon-btn-danger" onClick={() => handleViderLot(row)} data-tooltip-id="tooltip" data-tooltip-content="Vider le lot périmé">
                <FontAwesomeIcon icon={faTrash} />
              </button>
            )}
          </div>
        );
      }
    }
  ];

  useEffect(() => {
    if (show && productId) {
      fetchProductBatches();
    }
  }, [show, productId]);

  const expiredBatchCount = selectedBatches.filter(isExpiredBatch).length;
  const visibleBatches = selectedBatches.filter(b => isExpiredBatch(b) === showExpiredBatches);
  const totalStock = selectedBatches.reduce((sum, b) => sum + (b.quantity || 0), 0);

  return (
    <>
      <Modal
        id="viewStockModal"
        classSize="modal-lg"
        title={productName ? `Lots · ${productName}` : 'Lots'}
        show={show}
        onClose={onClose}
        icon={faLayerGroup}
        iconBg="#f0fdfa"
        iconColor="#0d9488"
        noBodyPadding
        footer={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <span style={{ fontSize: 13, color: '#6b7280' }}>
              <strong style={{ color: '#111827' }}>{selectedBatches.length}</strong> lot{selectedBatches.length > 1 ? 's' : ''} ·{' '}
              stock total <strong style={{ color: '#111827' }}>{totalStock}</strong>
            </span>
            <button type="button" className="btn btn-light btn-sm" onClick={onClose}>
              Fermer
            </button>
          </div>
        }
        headerAction={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {expiredBatchCount > 0 && (
              <button
                type="button"
                className={`btn btn-sm ${showExpiredBatches ? 'btn-primary' : 'btn-light'}`}
                style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
                onClick={() => setShowExpiredBatches(v => !v)}
              >
                <FontAwesomeIcon icon={faFilter} />
                {showExpiredBatches ? 'Masquer les lots périmés' : `Lots périmés (${expiredBatchCount})`}
              </button>
            )}
            {canEdit && (
              <button
                type="button"
                className="btn btn-light btn-sm"
                style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
                onClick={() => setCreateBatchModal(true)}
              >
                <FontAwesomeIcon icon={faCirclePlus} />
                Nouveau lot
              </button>
            )}
          </div>
        }
      >
        <Table
          mode="client"
          rowId="id"
          columns={columns}
          data={visibleBatches}
          pageSize={20}
          showViewsButton={false}
          filters={readPersistedFilters(productId)}
          onFilter={(values) => persistFilters(productId, values)}
          onReset={() => persistFilters(productId, {})}
          multiSelect={canEdit}
          selectionActions={canEdit ? [
            { label: 'Vider les lots périmés', icon: faTrash, variant: 'danger', onClick: handleBulkVider },
          ] : []}
        />
      </Modal>
      <StockBatchMovementModal
        batchId={selectedBatchId}
        productName={productName}
        batch={selectedBatches.find(b => b.id === selectedBatchId)}
        show={stockBatchMovementModal}
        onClose={() => setStockBatchMovementModal(false)}
      />
      <StockEditModal
          batchId={selectedBatchId}
          orderableId={productId}
          action={movementType}
          show={createMovementModal}
          onClose={() => setCreateMovementModal(false)}
          // stock in / out changes the batch quantity and the product's total stock: refresh
          // this lots list and the listing behind it (Products / laboratory / product page)
          onSuccess={() => { fetchProductBatches(); onBatchUpdate?.(); }}
        />
      <EditBatchModal
          batch={selectedBatch}
          show={editBatchModal}
          onClose={() => setEditBatchModal(false)}
          onSuccess={() => { fetchProductBatches(); onBatchUpdate?.(); }}
        />
      <CreateStockModal
          stock={{ orderableId: productId }}
          show={createBatchModal}
          onClose={() => setCreateBatchModal(false)}
          onSave={() => { fetchProductBatches(); onBatchUpdate?.(); }}
        />
    </>
  );
}
